import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Button, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Rating, Stack, TextField, Typography,
} from "@mui/material";
import { MdAdd, MdDelete, MdEdit, MdRefresh, MdSearch } from "react-icons/md";
import { getAllReviews, deleteReview } from "../store/actions/reviews";
import AddReviewsDialog from "../components/modals/AddReviewsDialog";
import ReviewsEditModal from "../components/modals/ReviewsEditModal";
import Colors from "../../assets/styles/Colors";

export default function ReviewsView() {
  const dispatch = useDispatch();
  const { reviews } = useSelector((s) => s.reviews);

  const isFirstUse = useRef(true);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem]   = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting]           = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    dispatch(getAllReviews());
    setTimeout(() => setLoading(false), 800);
  }, [dispatch]);

  useEffect(() => {
    if (isFirstUse.current) {
      isFirstUse.current = false;
      refresh();
    }
  }, [refresh]);

  const filtered = (() => {
    if (!reviews) return [];
    if (!search.trim()) return reviews;
    const q = search.toLowerCase();
    return reviews.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.text || "").toLowerCase().includes(q)
    );
  })();

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteReview(confirmDelete.id);
      setConfirmDelete(null);
      refresh();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`, border: `1px solid ${Colors.borderSecondary}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ color: Colors.textPrimary }}>Reseñas</Typography>
              <Typography variant="body2" sx={{ color: Colors.textSecondary }}>
                Testimonios de usuarios mostrados en la landing
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <IconButton onClick={refresh} sx={{ color: Colors.textSecondary }}>
                <MdRefresh size={20} />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<MdAdd />}
                onClick={() => setAddOpen(true)}
                sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
              >
                Nueva Reseña
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Search */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
          <TextField
            size="small"
            placeholder="Buscar por nombre o reseña..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <MdSearch style={{ marginRight: 6, color: Colors.textSecondary }} /> }}
            sx={{
              minWidth: 300,
              "& .MuiOutlinedInput-root": {
                backgroundColor: Colors.bgSecondary,
                color: Colors.textPrimary,
                "& fieldset": { borderColor: Colors.borderSecondary },
                "&:hover fieldset": { borderColor: Colors.borderPrimary },
              },
            }}
          />
          {reviews && (
            <Typography variant="caption" sx={{ color: Colors.textSecondary }}>
              {filtered.length} de {reviews.length}
            </Typography>
          )}
        </Stack>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: Colors.primary }} />
          </Box>
        )}

        {!loading && (
          <Paper sx={{ borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, overflow: "hidden" }}>
            {/* Table header */}
            <Box sx={{ px: 3, py: 1.5, backgroundColor: Colors.bgTertiary, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 48, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Avatar</Typography>
                </Box>
                <Box sx={{ flex: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Nombre</Typography>
                </Box>
                <Box sx={{ width: 130, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Estrellas</Typography>
                </Box>
                <Box sx={{ flex: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Reseña</Typography>
                </Box>
                <Box sx={{ width: 60, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Orden</Typography>
                </Box>
                <Box sx={{ width: 90, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Acciones</Typography>
                </Box>
              </Stack>
            </Box>

            {filtered.length === 0 && (
              <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                <Typography sx={{ color: Colors.textSecondary }}>Sin reseñas.</Typography>
              </Box>
            )}

            {filtered.map((review) => (
              <Box
                key={review.id}
                sx={{
                  px: 3, py: 1.75,
                  borderBottom: `1px solid ${Colors.borderSecondary}`,
                  "&:hover": { backgroundColor: Colors.bgTertiary },
                  transition: "background-color 0.15s",
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 48, flexShrink: 0 }}>
                    {review.avatarURL
                      ? <img src={review.avatarURL} alt="" width={36} height={36} style={{ borderRadius: "50%", objectFit: "cover", display: "block" }} />
                      : <Box sx={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: Colors.bgTertiary, border: `1px solid ${Colors.borderSecondary}` }} />
                    }
                  </Box>
                  <Box sx={{ flex: 1.5 }}>
                    <Typography variant="body2" sx={{ color: Colors.textPrimary }}>{review.name || "—"}</Typography>
                  </Box>
                  <Box sx={{ width: 130, flexShrink: 0 }}>
                    <Rating value={review.stars || 5} readOnly size="small" />
                  </Box>
                  <Box sx={{ flex: 3, overflow: "hidden" }}>
                    <Typography
                      variant="caption"
                      sx={{ color: Colors.textSecondary, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}
                    >
                      {review.text || "—"}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 60, flexShrink: 0 }}>
                    <Typography variant="body2" sx={{ color: Colors.textSecondary }}>{review.order ?? "—"}</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} sx={{ width: 90, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => { setSelectedItem(review); setEditModalOpen(true); }}
                      sx={{ color: Colors.textSecondary }}
                      title="Editar"
                    >
                      <MdEdit size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setConfirmDelete(review)}
                      sx={{ color: Colors.error }}
                      title="Eliminar"
                    >
                      <MdDelete size={16} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Paper>
        )}

        {/* Dialogs & modals */}
        <AddReviewsDialog open={addOpen} onClose={() => setAddOpen(false)} refresh={refresh} />

        {selectedItem && (
          <ReviewsEditModal
            open={editModalOpen}
            onClose={() => { setEditModalOpen(false); setSelectedItem(null); }}
            data={selectedItem}
            refresh={refresh}
          />
        )}

        {/* Confirm delete dialog */}
        <Dialog
          open={!!confirmDelete}
          onClose={() => !deleting && setConfirmDelete(null)}
          PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` } }}
        >
          <DialogTitle sx={{ color: Colors.textPrimary }}>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: Colors.textSecondary }}>
              ¿Eliminar la reseña de "{confirmDelete?.name}"? Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)} disabled={deleting} sx={{ color: Colors.textSecondary }}>
              Cancelar
            </Button>
            <Button onClick={confirmDeleteAction} disabled={deleting} sx={{ color: Colors.error }}>
              {deleting ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
}
